import { useMemo } from 'react';

import { IFilterDefinition, ISelectFilterDefinition, OptionType } from '@evinced-private/ui-common';

import { ENVIRONMENT_LABEL_KEY } from 'src/common/consts/platformConfigConsts';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { LabelsExcludedFromDashboard } from 'src/common/types/ConfigurationTypes';

import { DASHBOARD_DATE_RANGE_OPTIONS } from '../constants/DashboardFiltersDefinitions';
import {
	buildLabelsExcludedFromDashboard,
	processPlatformConfiguration
} from '../helpers/ConfigurationProcessingHelpers';
import { sanitizeConfigurationLabel } from '../helpers/DataSanitizationHelpers';
import { createSingleSelectEnvironmentFilter } from '../helpers/FilterCreationHelpers';

import { useDistinctEnvironmentsQuery } from './useDistinctEnvironmentsQuery';

const DATE_RANGE_FILTER: ISelectFilterDefinition = {
	id: 'dateRange',
	label: 'Date range',
	filterType: 'select',
	defaultValue: [DASHBOARD_DATE_RANGE_OPTIONS[0]],
	componentProps: {
		placeholder: 'Last 7 days',
		isMultiple: false,
		id: 'dateRange',
		label: 'Date range',
		listboxWidth: '180px'
	}
};

export const useConfigurationFilters = (): {
	filterDefinitions: IFilterDefinition[];
	filterOptions: Record<string, OptionType[]>;
	labelsExcludedFromDashboard: LabelsExcludedFromDashboard;
} => {
	const { configuration } = useConfiguration();
	const { data: distinctEnvironments } = useDistinctEnvironmentsQuery();

	const { filterDefinitions, filterOptions, labelsExcludedFromDashboard } = useMemo(() => {
		if (!configuration?.PLATFORM) {
			return {
				filterDefinitions: [DATE_RANGE_FILTER],
				filterOptions: { dateRange: DASHBOARD_DATE_RANGE_OPTIONS },
				labelsExcludedFromDashboard: {}
			};
		}

		const { coreLabels, isValid } = processPlatformConfiguration(configuration.PLATFORM);

		if (!isValid) {
			return {
				filterDefinitions: [DATE_RANGE_FILTER],
				filterOptions: { dateRange: DASHBOARD_DATE_RANGE_OPTIONS },
				labelsExcludedFromDashboard: {}
			};
		}

		const environmentLabel = coreLabels.find((label) => label.label === ENVIRONMENT_LABEL_KEY);

		const allFilters: ISelectFilterDefinition[] = [];
		const allOptions: Record<string, OptionType[]> = {
			dateRange: DASHBOARD_DATE_RANGE_OPTIONS
		};

		if (environmentLabel) {
			const sanitized = sanitizeConfigurationLabel(environmentLabel);

			if (sanitized.values.length > 0) {
				const permittedOptions: OptionType[] = sanitized.values.map((value) => ({
					value: String(value),
					label: String(value)
				}));

				const permittedValues = new Set(permittedOptions.map((o) => o.value));
				const additionalOptions: OptionType[] = (distinctEnvironments ?? [])
					.filter((env) => !permittedValues.has(env))
					.map((env) => ({ value: env, label: env }));

				const environmentFilter = createSingleSelectEnvironmentFilter(
					sanitized.displayName || sanitized.label || 'Environment',
					[]
				);

				allFilters.push(environmentFilter);
				allOptions[ENVIRONMENT_LABEL_KEY] = [...permittedOptions, ...additionalOptions];
			}
		}
		allFilters.push(DATE_RANGE_FILTER);

		const labelsExcludedFromDashboard = buildLabelsExcludedFromDashboard(coreLabels);

		return {
			filterDefinitions: allFilters,
			filterOptions: allOptions,
			labelsExcludedFromDashboard
		};
	}, [configuration, distinctEnvironments]);

	return { filterDefinitions, filterOptions, labelsExcludedFromDashboard };
};
